import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, gbp } from "../lib/apiClient";
import { useAuth } from "../lib/AuthContext";
import { Search, Plus, Copy, Trash2, Filter, X, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { toast } from "../components/ui/sonner";
import { useConfirm } from "../components/ConfirmDialog";

function fmtDate(s) {
  if (!s) return "—";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "2-digit" });
}

export default function QuoteListPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ job_type: "", estimator: "", customer: "", depot: "" });
  const [options, setOptions] = useState({ estimators: [], customers: [], job_types: ["Repair", "Install"], depots: [] });
  const [busyId, setBusyId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const nav = useNavigate();
  const confirm = useConfirm();

  const setSort = (field) => {
    if (sortBy === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortDir(field === "grand_total" || field === "created_at" ? "desc" : "asc");
    }
  };

  const sortedItems = useMemo(() => {
    const copy = [...items];
    copy.sort((a, b) => {
      const av = a[sortBy];
      const bv = b[sortBy];
      const aNum = typeof av === "number" ? av : null;
      const bNum = typeof bv === "number" ? bv : null;
      let cmp;
      if (aNum !== null || bNum !== null) {
        cmp = (aNum ?? 0) - (bNum ?? 0);
      } else {
        cmp = String(av || "").localeCompare(String(bv || ""), undefined, { numeric: true, sensitivity: "base" });
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [items, sortBy, sortDir]);

  const load = () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    api.get(`/quotes?${params.toString()}`).then((r) => setItems(r.data));
  };

  // Initial load + load filter dropdown options
  useEffect(() => {
    load();
    api.get("/quotes/filter-options").then((r) => setOptions(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  // Re-fetch when filters change
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter(Boolean).length,
    [filters]
  );

  const setFilter = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const clearFilters = () => setFilters({ job_type: "", estimator: "", customer: "", depot: "" });

  const duplicate = async (qid) => {
    setBusyId(qid);
    try {
      const { data } = await api.post(`/quotes/${qid}/duplicate`);
      nav(`/quotes/${data.id}/edit`);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (q) => {
    const ok = await confirm({
      title: `Delete quote ${q.quote_number}?`,
      description: `For ${q.customer_name}. This cannot be undone.`,
      confirmText: "Delete",
      destructive: true,
    });
    if (!ok) return;
    setBusyId(q.id);
    try {
      await api.delete(`/quotes/${q.id}`);
      setItems((prev) => prev.filter((x) => x.id !== q.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete quote");
    } finally {
      setBusyId(null);
    }
  };

  const columns = (isAdmin
    ? [
        { key: "quote_number", label: "Quote #", sortable: true },
        { key: "customer_name", label: "Customer", sortable: true },
        { key: "job_type", label: "Job", sortable: true },
        { key: "branch", label: "Labour Type", sortable: true },
        { key: "estimator", label: "Estimator", sortable: true },
        { key: "grand_total", label: "Total", sortable: true, align: "right" },
        { key: "created_at", label: "Created", sortable: true },
        { key: "_actions", label: "", sortable: false, align: "right" },
      ]
    : [
        { key: "quote_number", label: "Quote #", sortable: true },
        { key: "customer_name", label: "Customer", sortable: true },
        { key: "job_type", label: "Job", sortable: true },
        { key: "estimator", label: "Estimator", sortable: true },
        { key: "grand_total", label: "Total", sortable: true, align: "right" },
        { key: "created_at", label: "Created", sortable: true },
        { key: "_actions", label: "", sortable: false, align: "right" },
      ]);

  const SortIcon = ({ active, dir }) => {
    if (!active) return <ArrowUpDown size={11} className="text-slate-300 group-hover:text-slate-500 transition-colors" />;
    return dir === "asc"
      ? <ArrowUp size={11} className="text-brand-blue" />
      : <ArrowDown size={11} className="text-brand-blue" />;
  };

  return (
    <div className="space-y-6 fade-up" data-testid="quotes-list-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 mb-2">Archive</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Calculations</h1>
        </div>
        {(user?.role === "admin" || (user?.pages || []).includes("quote_create")) && <Link
          to="/quotes/new"
          data-testid="new-quote-button"
          className="inline-flex items-center gap-2 h-10 px-5 bg-brand-blue text-white text-xs uppercase tracking-wider font-semibold hover:bg-brand-blue-dark"
        >
          <Plus size={14} /> New Quote
        </Link>}
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search by customer, quote # or description"
            className="h-10 w-full border border-slate-300 pl-9 pr-3 text-sm focus:outline-none focus:border-slate-900"
            data-testid="search-input"
          />
        </div>
        <button onClick={load} className="h-10 px-4 border border-slate-900 hover:bg-slate-900 hover:text-white text-xs uppercase tracking-wider font-semibold">
          Apply
        </button>
        <button
          onClick={() => setShowFilters((s) => !s)}
          className={`h-10 px-4 border text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-2 ${
            showFilters || activeFilterCount > 0
              ? "border-brand-blue text-brand-blue bg-blue-50"
              : "border-slate-300 hover:border-slate-900"
          }`}
          data-testid="toggle-filters-button"
        >
          <Filter size={14} />
          Filters
          {activeFilterCount > 0 && (
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-brand-blue text-white text-[10px] font-bold">
              {activeFilterCount}
            </span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <button
            onClick={clearFilters}
            className="h-10 px-3 text-xs uppercase tracking-wider font-semibold text-slate-500 hover:text-red-600 inline-flex items-center gap-1"
            data-testid="clear-filters-button"
          >
            <X size={14} /> Clear
          </button>
        )}
      </div>

      {showFilters && (
        <div className="qb-card p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="filters-panel">
          <FilterField label="Job Type">
            <select
              value={filters.job_type}
              onChange={(e) => setFilter("job_type", e.target.value)}
              className="h-9 w-full border border-slate-300 px-2 text-sm bg-white"
              data-testid="filter-job-type"
            >
              <option value="">All</option>
              {options.job_types.map((j) => <option key={j} value={j}>{j}</option>)}
            </select>
          </FilterField>
          <FilterField label="Estimator">
            <select
              value={filters.estimator}
              onChange={(e) => setFilter("estimator", e.target.value)}
              className="h-9 w-full border border-slate-300 px-2 text-sm bg-white"
              data-testid="filter-estimator"
            >
              <option value="">All</option>
              {options.estimators.map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
          </FilterField>
          <FilterField label="Customer">
            <select
              value={filters.customer}
              onChange={(e) => setFilter("customer", e.target.value)}
              className="h-9 w-full border border-slate-300 px-2 text-sm bg-white"
              data-testid="filter-customer"
            >
              <option value="">All</option>
              {options.customers.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </FilterField>
          {isAdmin && (
            <FilterField label="Branch (Depot)">
              <select
                value={filters.depot}
                onChange={(e) => setFilter("depot", e.target.value)}
                className="h-9 w-full border border-slate-300 px-2 text-sm bg-white"
                data-testid="filter-depot"
              >
                <option value="">All</option>
                {options.depots.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </FilterField>
          )}
        </div>
      )}

      <div className="qb-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="bg-slate-50/60 border-b border-slate-100">
              {columns.map((c) => (
                <th
                  key={c.key}
                  className={`px-4 py-2.5 text-[10px] uppercase tracking-wider font-semibold text-slate-500 ${c.align === "right" ? "text-right" : "text-left"}`}
                >
                  {c.sortable ? (
                    <button
                      type="button"
                      onClick={() => setSort(c.key)}
                      className={`group inline-flex items-center gap-1.5 hover:text-slate-900 transition-colors ${sortBy === c.key ? "text-slate-900" : ""}`}
                      data-testid={`sort-${c.key}`}
                      title={`Sort by ${c.label}`}
                    >
                      <span>{c.label}</span>
                      <SortIcon active={sortBy === c.key} dir={sortDir} />
                    </button>
                  ) : (
                    c.label
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedItems.length === 0 && (
              <tr><td colSpan={columns.length} className="p-10 text-center text-slate-400 text-sm">No quotes found.</td></tr>
            )}
            {sortedItems.map((q) => (
              <tr key={q.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors" data-testid={`quote-row-${q.id}`}>
                <td className="px-4 py-3 font-mono">{q.quote_number}</td>
                <td className={`px-4 py-3 font-medium ${q.approval_required && !q.approved ? "text-red-600" : ""}`} data-testid={`quote-customer-${q.id}`}>
                  {q.customer_name}
                  {q.approval_required && !q.approved && (
                    <span
                      className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold bg-red-50 text-red-700 border border-red-300 align-middle"
                      title="This quote's labour type needs approving before it can be printed or exported."
                      data-testid={`awaiting-approval-badge-${q.id}`}
                    >
                      Awaiting approval
                    </span>
                  )}
                  {q.site_name && (
                    <div className="text-[11px] text-slate-500 font-normal mt-0.5">{q.site_name}</div>
                  )}
                  {q.has_overrides && (
                    <span
                      className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 border border-amber-300 align-middle"
                      title="One or more lines on this quote have an override stamp (hours below default or markup below floor)."
                      data-testid={`overrides-badge-${q.id}`}
                    >
                      Overrides
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-slate-600">{q.job_type}</td>
                {isAdmin && <td className="px-4 py-3 text-slate-600">{q.branch || "—"}</td>}
                <td className="px-4 py-3 text-slate-600">
                  {q.estimator || "—"}
                  {isAdmin && q.created_by_name && q.estimator && q.created_by_name.trim().toLowerCase() !== q.estimator.trim().toLowerCase() && (
                    <div className="text-[10px] uppercase tracking-wider text-brand-orange font-semibold mt-0.5" title="The 'Estimator' name on the quote doesn't match the signed-in user who created it." data-testid={`estimator-mismatch-${q.id}`}>
                      Created by {q.created_by_name}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 font-mono font-semibold text-right">{gbp(q.grand_total)}</td>
                <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-testid={`created-${q.id}`}>{fmtDate(q.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="inline-flex items-center gap-3">
                    <button
                      onClick={() => duplicate(q.id)}
                      disabled={busyId === q.id}
                      className="text-xs uppercase tracking-wider text-slate-500 hover:text-brand-blue font-semibold inline-flex items-center gap-1 disabled:opacity-40"
                      data-testid={`duplicate-row-${q.id}`}
                      title="Duplicate as a new calculation"
                    >
                      <Copy size={12} /> {busyId === q.id ? "…" : "Duplicate"}
                    </button>
                    <Link to={`/quotes/${q.id}`} className="text-xs uppercase tracking-wider text-brand-blue hover:underline font-semibold">Open</Link>
                    {isAdmin && (
                      <button
                        onClick={() => remove(q)}
                        disabled={busyId === q.id}
                        className="text-slate-400 hover:text-red-600 disabled:opacity-40"
                        data-testid={`delete-row-${q.id}`}
                        title="Delete quote"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold block mb-1">{label}</label>
      {children}
    </div>
  );
}
